import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import { MaterialSymbol } from '@/components/MaterialSymbol';

export type OverBudgetAlertProps = {
  /** The bold line: by how much it is over. */
  title: ReactNode;
  /** The quieter follow-on: what to do about it. */
  advice?: ReactNode;
  automationId: string;
};

/**
 * The coral over-budget alert, as the contract's `OverBudgetBanner` draws it.
 * The task-level allocation warning used it from 2026-10-14 to 2026-10-18,
 * then moved to the theme's yellow `Alert severity="warning"`: over-allocating
 * a task is a heads-up, not an error, and red read as blocking.
 */
export const OverBudgetAlert = ({ title, advice, automationId }: OverBudgetAlertProps) => (
  <Box
    role="alert"
    data-automation-id={automationId}
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1,
      px: 1.5,
      py: 1,
      bgcolor: 'var(--color-coral-10)',
      border: 'var(--border-width-thin) solid var(--color-coral-40)',
      borderRadius: 'var(--border-radius-small)',
      mt: 1,
    }}
  >
    <MaterialSymbol name="warning" size={16} color="var(--color-coral-80)" />
    <Box component="span" sx={{ fontSize: 14, color: 'var(--color-coral-100)', fontWeight: 500 }}>
      {title}
    </Box>
    {advice ? (
      <Box component="span" sx={{ fontSize: 11, color: 'var(--color-coral-80)' }}>
        {advice}
      </Box>
    ) : null}
  </Box>
);
